import { useEffect, useRef, useState } from 'react';
import './Projects.css';

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); obs.disconnect(); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const featured = {
  name: 'Rota',
  period: 'déc. 2025 - aujourd\'hui',
  status: 'En développement',
  tagline: 'L\'écosystème qui réconcilie le bureau et la route.',
  description:
    'Ancien livreur, j\'ai vécu de l\'intérieur le chaos de la gestion de flotte. Rota est la solution que j\'aurais voulu avoir. Un SaaS de management (tournées, livreurs, KPIs) couplé à une app mobile pour les chauffeurs, avec déclaration d\'incidents géolocalisée et génération PDF horodatée.',
  highlight: 'Saiya Mach 1 : IA embarquée qui compare automatiquement les inspections photo pour détecter tout nouvel impact sur un véhicule. Zéro expertise requise côté gestionnaire.',
  tags: ['React Native', 'TypeScript', 'Node.js', 'IA', 'SaaS', 'App Mobile'],
  link: null,
  github: null,
};

const projects = [
  {
    name: 'Keysper',
    period: 'janv. 2025 - août 2025',
    description:
      'Refonte complète d\'une plateforme immobilière pour étudiants. App mobile native avec un système de swipe inspiré des apps de rencontre, gestion multi-profils avec coffres-forts numériques pour documents administratifs, et sécurisation totale de l\'API existante.',
    tags: ['React Native', 'TypeScript', 'Node.js', 'Express.js', 'JWT', 'Bcrypt'],
    link: null,
    github: null,
  },
  {
    name: 'FaceR',
    period: 'déc. 2024 - janv. 2025',
    description:
      'Système de surveillance urbaine en Python : reconnaissance faciale en temps réel, détection d\'émotions et identification d\'individus sur base de données. Projet de recherche explorant les limites technologiques et éthiques de la vision par ordinateur.',
    tags: ['Python', 'OpenCV', 'Face Recognition', 'Computer Vision'],
    link: null,
    github: null,
  },
  {
    name: 'IntelliSearch',
    period: 'juin 2024 - déc. 2024',
    description:
      'Interface intuitive de Google Dorking : rend accessibles les opérateurs de recherche avancée pour le pentesting, le marketing ou la veille. 561 requêtes en 24h dès le lancement.',
    tags: ['React', 'TypeScript'],
    link: null,
    github: null,
    stat: '561 requêtes · J+1',
  },
  {
    name: 'Alpha R6 SILA',
    period: '2025',
    description:
      'Moteur de recherche distribué pour le darkweb. Crawler Rust async (120 workers, 4 circuits Tor isolés), découverte automatique des .onion v3, file persistée SQLite avec dédoublonnage SHA-256 et rotation d\'identité Tor adaptative. Indexation Quickwit en NDJSON, frontend React/Redux pour requêter et filtrer.',
    tags: ['Rust', 'Tokio', 'Quickwit', 'Tor', 'SQLite', 'React', 'Redux', 'TypeScript'],
    link: null,
    github: null,
    stat: '~4–8M pages / semaine',
  },
];

function ProjectCard({ project, index }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={`project-card${inView ? ' is-visible' : ''}`}
      style={{ transitionDelay: `${index * 0.08}s` }}
    >
      <div className="project-card__top">
        <span className="project-card__period">{project.period}</span>
        <div className="project-card__actions">
          {project.github && (
            <a href={project.github} className="project-card__icon" aria-label="GitHub" target="_blank" rel="noreferrer">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/>
              </svg>
            </a>
          )}
          {project.link && (
            <a href={project.link} className="project-card__icon" aria-label="Voir le projet" target="_blank" rel="noreferrer">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                <polyline points="15 3 21 3 21 9"/>
                <line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
            </a>
          )}
        </div>
      </div>
      <h3 className="project-card__name">{project.name}</h3>
      {project.stat && <p className="project-card__stat">{project.stat}</p>}
      <p className="project-card__desc">{project.description}</p>
      <div className="project-card__tags">
        {project.tags.map(t => (
          <span key={t} className="skill-tag">{t}</span>
        ))}
      </div>
    </div>
  );
}

function FeaturedCard() {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} className={`featured-card${inView ? ' is-visible' : ''}`}>
      <div className="featured-card__header">
        <div>
          <span className="featured-card__status">{featured.status}</span>
          <h3 className="featured-card__name">{featured.name}</h3>
          <p className="featured-card__period">{featured.period}</p>
        </div>
        <p className="featured-card__tagline">{featured.tagline}</p>
      </div>
      <p className="featured-card__desc">{featured.description}</p>
      <div className="featured-card__highlight">
        <span className="featured-card__highlight-label">IA</span>
        <p>{featured.highlight}</p>
      </div>
      <div className="featured-card__tags">
        {featured.tags.map(t => (
          <span key={t} className="skill-tag">{t}</span>
        ))}
      </div>
    </div>
  );
}

export default function Projects() {
  return (
    <section className="projects section" id="projects">
      <p className="section__label">Projets</p>
      <h2 className="section__title">
        Ce que j'ai <span className="keyword">construit</span>
      </h2>
      <FeaturedCard />
      <div className="projects__grid">
        {projects.map((p, i) => (
          <ProjectCard key={i} project={p} index={i} />
        ))}
      </div>
    </section>
  );
}
